import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { OrderListItem } from "../../common/orderListItem/OrderListItem";
import OrderDetails from "../../common/orderDetails/OrderDetails";
import {
  buttonContainerStyle,
  mainContainerStyle,
  cancelButtonStyle,
  createInvoiceButtonStyle,
  includeServiceInvoiceStyle,
  saveButtonStyle,
  saveButtonStyle1,
} from "./OrderSummaryStyles";
import { orderSummaryStages } from "../../constants/orderSummary";
import {
  Order,
  OrderDetails as OrderDetailsProps,
} from "../../dto/orderSummaryTypes";
import { productEndPoints } from "../../constants/endpoints";
import { UpdateOptions } from "../../common/orderDetails/UpdateOptions";
import ConfirmationDialog from "../../common/confirmationDialog/ConfirmationDialog";
import { DatePicker } from "../../common/orderDetails/DatePicker";
import { dropdownTitle } from "../../common/orderDetails/orderDetailsStyles";
import { Switch } from "../../common/Switch";
import Toast from "../../common/Toast";
import moment from "moment";
import { sendCustomEvents } from "../../ga";
import EditProductModal from "../editProduct/EditProduct";
import { fetchProductListingData } from "../../api/ProductListAPI";
import { mapProducts } from "../../utils/productListMapping";
import { ProductListingProps } from "../../types/ProductList";
import {
  updateOrderAPI,
  fetchOrderDetails,
  toggleIncludeService,
  editOrderAPI,
  downloadInvoiceAPI,
} from "../../api/orderFinalAPI";
import { ActionButton } from "../../common/actionButton/ActionButton";
import PaymentDialog from "../../common/paymentDialog";
import Colors from "../../theme/Colours";
import { mapErrorMessage } from "../../utils/updateErrorMapper";

const OrderSummary = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = location.pathname.split("/").pop();

  const [orderDetails, setOrderDetails] = useState<OrderDetailsProps>();
  const [isOrderCancelled, setIsOrderCancelled] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [showDownloadInvoiceError, setShowDownloadInvoiceError] =
    useState(false);
  const [includeServiceInvoice, setIncludeServiceInvoice] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const orgId = localStorage.getItem("orgId");

  const getOrderDetails = async () => {
    if (!orderId) return;
    setIsLoading(true);
    try {
      const data = await fetchOrderDetails(orderId);
      if (data) {
        setOrderDetails(data.data);
        setOrders(data.data.orders ?? []);
        setIncludeServiceInvoice(!!data.data.includeService);
        const stageIndex = orderSummaryStages.findIndex(
          (stage) => stage.title === data.data.status
        );
        data?.data?.eta && setSelectedDate(moment(data.data.eta));
        setCurrentStage(stageIndex);

        if (data.data.status.toUpperCase() === "CANCELLED") {
          setIsOrderCancelled(true);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const isCancellationAllowed = currentStage < 3;

  useEffect(() => {
    if (!orderDetails) getOrderDetails();
  }, []);

  const buttonLabel: "Save" | "Create Invoice" =
    currentStage + 1 > 3 ? "Create Invoice" : "Save";

  const [showCancelOrder, setShowCancelOrder] = useState(false);

  const cancelOrder = async () => {
    setShowCancelOrder(false);
    try {
      const response = await editOrderAPI(orderId!, {
        newStatus: "CANCELLED",
      });

      if (response?.status === 201) {
        if (response.data.data?.order?.orderStatus === "CANCELLED") {
          setIsOrderCancelled(true);
          setToastMessage("Order has been Cancelled");

          setTimeout(() => {
            navigate(`/maltora-seller/orders`);
          }, 1000);
        }
      }
    } catch (error) {
      console.error(error);
    }
    setShowToast(true);
  };

  const saveOrder = async (showToastMsg = true) => {
    setError(false);
    try {
      const formattedEta = selectedDate?.startOf("day").format("YYYY-MM-DD");
      const response = await editOrderAPI(orderId!, {
        newStatus: orderSummaryStages[currentStage].title
          .toLocaleUpperCase()
          .replaceAll(" ", "_"),
        eta: formattedEta,
      });
      if (response.data) {
        if (showToastMsg) {
          setToastMessage(
            "Order status has been successfully updated and saved!"
          );
        }
      } else {
        setError(true);
        setToastMessage(
          "Failed to update order status. Please try again later."
        );
      }
    } catch (error) {
      setError(true);
      setToastMessage("Failed to update order status. Please try again later.");
      console.error(error);
    }
    if (showToastMsg) setShowToast(true);
  };

  const createInvoice = async () => {
    setTimeout(() => {
      navigate(`/maltora-seller/invoice-screen/${orderId}`, {
        state: { openEdit: true },
      });
      window.scrollTo(0, 0);
    }, 1500);
  };

  const downloadInvoice = async () => {
    try {
      const response = await downloadInvoiceAPI(orderId!);
      if (response.data.data) {
        const link = document.createElement("a");
        link.href = response.data.data;
        link.download = `invoice-${orderId}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setShowDownloadInvoiceError(true);
      }
    } catch (error) {
      console.error(error);
      setShowDownloadInvoiceError(true);
    }
  };

  const mainAction = () => {
    switch (buttonLabel) {
      case "Save":
        saveOrder();
        break;
      case "Create Invoice":
        createInvoice();
        saveOrder(false);
        break;
      default:
        console.log("Button clicked");
    }
  };

  const [selectedDate, setSelectedDate] = useState<moment.Moment | null>(null);
  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const [editingOrder, setEditingOrder] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [orders, setOrders] = useState<OrderDetailsProps["orders"]>([]);

  const [modalMode, setModalMode] = useState<"edit" | "add">("edit");

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);

  const [products, setProducts] = useState<ProductListingProps[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Order>();

  useEffect(() => {
    if (showEditModal && products.length === 0) {
      const fetchProducts = async () => {
        try {
          const data = await fetchProductListingData(
            productEndPoints.getProducts
          );
          const mappedData = mapProducts(data.products);
          setProducts(mappedData);
        } catch (error) {
          console.error("Error fetching product data:", error);
        }
      };
      fetchProducts();
    }
  }, [showEditModal]);

  const edititngOptions = [
    {
      title: "Edit Order Details",
      action: () => setEditingOrder(true),
      isOutlined: false,
      visible: currentStage < 3 && !editingOrder,
    },
    {
      title: "Done",
      action: () => updateOrder(),
      isOutlined: false,
      visible: currentStage < 3 && editingOrder,
    },
    {
      title: "Reset",
      action: () => {
        setOrders(orderDetails?.orders ?? []);
        setEditingOrder(false);
      },
      isOutlined: true,
      visible: currentStage < 3 && editingOrder,
    },
    {
      title: "Add Product",
      action: () => {
        setModalMode("add");
        setSelectedProduct(createEmptyOrder());
        setShowEditModal(true);
      },
      isOutlined: false,
      visible: currentStage < 3 && editingOrder,
    },
  ];

  const createEmptyOrder = (): Order => ({
    id: `temp_${Date.now()}`,
    name: "",
    quantity: 20,
    liters: 1,
    image: "",
    productId: "",
  });

  const editOrder = (id: string) => {
    setModalMode("edit");
    if (isOrderCancelled) return;
    setShowEditModal(true);

    const orderToEdit = orders.find((item) => item.id === id);

    if (orderToEdit) {
      setSelectedProduct({
        ...orderToEdit,
      });
    }
  };

  const confirmEditOrder = () => {
    if (!selectedProduct) return;

    if (!selectedProduct.productId || !selectedProduct.name) {
      setToastMessage("Please select a product");
      setShowToast(true);
      return;
    }

    if (modalMode === "add") {
      const isDuplicate = orders.some(
        (order) =>
          order.productId === selectedProduct.productId &&
          order.quantity === selectedProduct.quantity
      );

      if (isDuplicate) {
        setToastMessage(
          "This product with the same keg size already exists in the order"
        );
        setShowToast(true);
        return;
      }

      const newProduct = {
        ...selectedProduct,
        id: selectedProduct.id.startsWith("temp_")
          ? selectedProduct.id
          : `temp_${Date.now()}`,
      };
      setOrders([...orders, newProduct]);
    } else {
      const isDuplicate = orders.some(
        (order) =>
          order.id !== selectedProduct.id &&
          order.productId === selectedProduct.productId &&
          order.quantity === selectedProduct.quantity
      );

      if (isDuplicate) {
        setToastMessage(
          "This product with the same keg size already exists in another item. Please use a different keg size."
        );
        setShowToast(true);
        return;
      }
      const updatedOrders = orders.map((order) => {
        if (order.id === selectedProduct.id) {
          return {
            ...order,
            name: selectedProduct.name,
            quantity: selectedProduct.quantity,
            liters: selectedProduct.liters,
            productId: selectedProduct.productId,
            image: selectedProduct.image,
          };
        }
        return order;
      });
      setOrders(updatedOrders);
    }

    setShowEditModal(false);
  };
  const updateOrder = async () => {
    if (!orderDetails) return;
    setEditingOrder(false);

    try {
      const payload = {
        orderDetails: orders.map((order) => ({
          productId: order.productId,
          quantity: order.quantity,
          liters: order.liters,
        })),
      };

      const response = await updateOrderAPI(orderDetails.id, payload);

      if (response.status === 201) {
        await getOrderDetails();
        setToastMessage("Order updated successfully!");
        setShowToast(true);
        setError(false);
      }
    } catch (err: any) {
      const rawErrorMessage =
        err?.response?.data?.error?.message ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update order.";

      const customErrorMessage = mapErrorMessage(rawErrorMessage, products);

      setOrders(orderDetails?.orders ?? []);

      setToastMessage(customErrorMessage);
      setShowToast(true);
      setError(true);
      console.error("Error while updating order:", err);
    }
  };

  const handleDeleteRequest = (id: string) => {
    setSelectedDeleteId(id);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!selectedDeleteId) return;

    const updatedOrders = orders.filter(
      (order) => order.id !== selectedDeleteId
    );
    setOrders(updatedOrders);
    if (updatedOrders.length === 0) {
      setToastMessage(
        "All products removed. Order will be cancelled when saved."
      );
    } else {
      setToastMessage("Product removed from order");
    }

    setShowDeleteDialog(false);
    setSelectedDeleteId(null);
    setShowToast(true);
  };

  const handleToggleIncludeService = async () => {
    if (!orderDetails) return;
    try {
      const newValue = !includeServiceInvoice;
      await toggleIncludeService(orderDetails.id, newValue);
      setIncludeServiceInvoice(newValue);
      setError(false);
      await saveOrder(false);
      await getOrderDetails();
    } catch (error) {
      setError(true);
      console.error("Failed to update includeService:", error);
    }
  };

  const processingIndex = orderSummaryStages.findIndex(
    (stage) => stage.title === "Processing"
  );

  const justifyContentValue =
    currentStage <= processingIndex ? "space-between" : "flex-start";

  return (
    <>
      <ConfirmationDialog
        buttonAction={cancelOrder}
        buttonTitle="Yes, I Am Sure"
        show={showCancelOrder}
        setShow={setShowCancelOrder}
        title="Are you sure you want to cancel?"
      />

      <ConfirmationDialog
        buttonAction={() => {
          downloadInvoice();
          setShowDownloadInvoiceError(false);
        }}
        buttonTitle="Retry"
        error={true}
        show={showDownloadInvoiceError}
        setShow={setShowDownloadInvoiceError}
        title="Invoice not found"
      />

      {selectedProduct && (
        <EditProductModal
          show={showEditModal}
          setShow={setShowEditModal}
          products={products}
          selectedItem={selectedProduct}
          setSelectedItem={setSelectedProduct}
          onConfirm={confirmEditOrder}
          mode={modalMode}
        />
      )}

      {orderDetails ? (
        <>
          <Box display={"flex"} flexDirection={"row-reverse"} gap={"18px"}>
            {edititngOptions.map(
              ({ action, isOutlined, title, visible }) =>
                visible &&
                !isOrderCancelled && (
                  <ActionButton
                    label={title}
                    key={`edit_${title}`}
                    onClick={action}
                    outlined={isOutlined}
                    sx={{
                      width:
                        title === "Edit Order Details" ||
                        title === "Add Product"
                          ? "163px"
                          : "94px",
                    }}
                  />
                )
            )}
          </Box>
          <Box sx={mainContainerStyle}>
            <Box>
              {orders?.map((order, index) => (
                <OrderListItem
                  key={index}
                  {...order}
                  updateItem={editOrder}
                  isEditable={editingOrder}
                  onDelete={handleDeleteRequest}
                />
              ))}
              <OrderDetails
                {...orderDetails}
                currentStage={currentStage}
                isOrderCancelled={isOrderCancelled}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: justifyContentValue,
                alignItems: "flex-end",
                gap: theme.spacing(2),
              }}
            >
              <UpdateOptions
                currentStage={currentStage}
                setCurrentStage={setCurrentStage}
                disabled={isOrderCancelled}
              />
              {isCancellationAllowed ? (
                <Box sx={buttonContainerStyle}>
                  <ActionButton
                    label="Cancel Order"
                    onClick={() => {
                      sendCustomEvents(
                        "Seller",
                        "Clicked Cancel Order",
                        "Cancel Order in Order summary"
                      );
                      setShowCancelOrder(true);
                    }}
                    outlined
                    disabled={isOrderCancelled}
                    sx={cancelButtonStyle}
                  />
                  <ActionButton
                    label="Save"
                    onClick={mainAction}
                    sx={saveButtonStyle1}
                    disabled={isOrderCancelled}
                  />
                </Box>
              ) : (
                <>
                  <DatePicker
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    disabled={isOrderCancelled}
                  />
                  <Box sx={{ flexDirection: "row" }}>
                    <Typography sx={dropdownTitle}>Invoice</Typography>
                    <ActionButton
                      label="Create Invoice"
                      onClick={mainAction}
                      outlined
                      sx={createInvoiceButtonStyle}
                      disabled={isOrderCancelled}
                    />
                  </Box>
                  <Box sx={{ flexDirection: "row" }}>
                    <Typography sx={includeServiceInvoiceStyle}>
                      Include Service Invoice
                    </Typography>
                    <Switch
                      checked={includeServiceInvoice}
                      onChange={handleToggleIncludeService}
                    />
                  </Box>
                </>
              )}
            </Box>

            {currentStage + 1 > 3 && (
              <ActionButton
                label="Save"
                onClick={() => saveOrder()}
                sx={saveButtonStyle}
                disabled={isOrderCancelled}
              />
            )}
          </Box>
        </>
      ) : isLoading ? null : (
        <Box sx={[mainContainerStyle, { textAlign: "center" }]}>
          <Typography>{`Order #${orderId} not found`}</Typography>
        </Box>
      )}
      {isOrderCancelled && (
        <Box
          sx={{
            backgroundColor: "#ffebee",
            border: `1px solid ${Colors.redText}`,
            borderRadius: 1,
            p: 2,
            mt: 3,
            textAlign: "center",
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          <Typography variant="h6" color={Colors.redText} fontWeight="bold">
            This order has been cancelled
          </Typography>
        </Box>
      )}

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
        data-testid={error ? "toast-error" : "toast-success"}
      />
      <PaymentDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title="Are you sure you want to delete this product from the order?"
        message=""
        primaryText="Yes"
        secondaryText="No"
        primaryAction={confirmDelete}
        secondaryAction={() => setShowDeleteDialog(false)}
        imageUrl={""}
      />
    </>
  );
};

export default OrderSummary;
